<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Plugin } from 'chart.js'
import { useRoute } from 'vue-router'
import { useTheme } from 'vuetify'
import { ERouteNames } from '@/router/ERouteNames'
import { geVerticalBarChartConfig, getLineChartAdverseEventRiskConfig } from '@core/libs/chartjs/chartjsConfig'
import { useChartColors } from '@/utils/chartColors'
import LineChart from '@core/libs/chartjs/components/LineChart'
import BarChart from '@core/libs/chartjs/components/BarChart'
import { deleteWidget } from '@/modules/dashboard/composables/deleteWidget'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import { useGraphStore } from '@/modules/dashboard/stores/graphStore'
import type { IBarChartWidget } from '@/modules/dashboard/types/IBarChartWidget'
import { hexToRgb } from '@core/utils/colorConverter'
import ChartSettingsMenu from '@/modules/dashboard/components/ChartSettingsMenu.vue'

interface Props {
  widgetData: IBarChartWidget
  currentTarget?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const route = useRoute()
const isMonitoringRoute = computed(() => route.name === ERouteNames.MONITORING)
const vuetifyTheme = useTheme()
const showMenu = ref(false)
const { generateColors } = useChartColors()

const hoveredDatasetIndex = ref<number | null>(null)
const hiddenDatasets = ref<number[]>([])

const originalColors = computed(() => {
  return generateColors(props.widgetData.data.datasets.length)
})

const getAlphaColor = (colorStr: string, opacity: number) => {
  if (!colorStr) return colorStr
  if (colorStr.startsWith('#')) {
    const rgb = hexToRgb(colorStr)
    return `rgba(${rgb}, ${opacity})`
  }
  if (colorStr.startsWith('rgb(')) {
    return colorStr.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`)
  }
  if (colorStr.startsWith('rgba(')) {
    return colorStr.replace(/,\s*[0-9.]+\)$/, `, ${opacity})`)
  }
  return colorStr
}

// Данные для линейного графика
const lineData = computed(() => {
  const originalData = props.widgetData.data
  const colors = originalColors.value

  const datasetsWithColors = originalData.datasets.map((dataset, index) => {
    const color = colors[index]
    const isHovered = hoveredDatasetIndex.value === null || hoveredDatasetIndex.value === index
    const opacity = isHovered ? 1.0 : 0.15
    const adjustedColor = getAlphaColor(color, opacity)

    return {
      ...dataset,
      backgroundColor: adjustedColor,
      borderColor: adjustedColor,
      pointBackgroundColor: adjustedColor,
      pointBorderColor: isHovered ? '#fff' : adjustedColor,
      pointBorderWidth: 2,
      pointHitRadius: 10,
      pointHoverRadius: 6,
      tension: 0.4,
      fill: false,
      hidden: hiddenDatasets.value.includes(index),
    }
  })

  return {
    ...originalData,
    datasets: datasetsWithColors,
  }
})

// Данные для столбчатого графика
const barData = computed(() => {
  const originalData = props.widgetData.data
  const colors = originalColors.value

  return {
    labels: originalData.labels,
    datasets: originalData.datasets.map((dataset, index) => {
      const color = colors[index]
      const isHovered = hoveredDatasetIndex.value === null || hoveredDatasetIndex.value === index
      const opacity = isHovered ? 1.0 : 0.15
      const adjustedColor = getAlphaColor(color, opacity)

      return {
        maxBarThickness: 15,
        label: dataset.label,
        backgroundColor: adjustedColor,
        borderColor: 'transparent',
        borderRadius: { topRight: 15, topLeft: 15 },
        data: dataset.data,
        hidden: hiddenDatasets.value.includes(index),
      }
    }),
  }
})

const graphStore = useGraphStore()
const chartType = ref<'bar' | 'line'>('bar')

const isBarChart = computed({
  get: () => chartType.value === 'bar',
  set: (val: boolean) => {
    chartType.value = val ? 'bar' : 'line'
  },
})

const selectedDateTrunc = computed({
  get: () => graphStore.dateTrunc || '',
  set: (val: string) => {
    if (props.currentTarget) {
      graphStore.setDateTrunc(props.currentTarget as any, val || null)
    }
  },
})

const showValues = ref(false)
const zoomScale = ref(100)

const barChartOptions = computed(() => {
  const baseConfig = geVerticalBarChartConfig(vuetifyTheme.current.value)
  return {
    ...baseConfig,
    interaction: {
      mode: 'nearest',
      intersect: true,
      axis: 'x' as const,
    },
    onHover: (event: any, activeElements: any, chart: any) => {
      if (!chart) return
      const chartArea = chart.chartArea
      if (!chartArea) return

      const isInside
        = event.x >= chartArea.left
        && event.x <= chartArea.right
        && event.y >= chartArea.top
        && event.y <= chartArea.bottom

      if (!isInside) {
        hoveredDatasetIndex.value = null
        return
      }

      const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true)
      if (elements && elements.length > 0) {
        hoveredDatasetIndex.value = elements[0].datasetIndex
      } else {
        hoveredDatasetIndex.value = null
      }
    },
    plugins: {
      ...baseConfig.plugins,
      tooltip: {
        ...(baseConfig.plugins as any)?.tooltip,
        intersect: true,
      },
      legend: {
        display: false,
      },
    },
  }
})
const lineChartConfig = computed(() => {
  const baseConfig = getLineChartAdverseEventRiskConfig(vuetifyTheme.current.value)
  return {
    ...baseConfig,
    interaction: {
      mode: 'nearest',
      intersect: true,
      axis: 'xy' as const,
    },
    onHover: (event: any, activeElements: any, chart: any) => {
      if (!chart) return
      const chartArea = chart.chartArea
      if (!chartArea) return

      const isInside
        = event.x >= chartArea.left
        && event.x <= chartArea.right
        && event.y >= chartArea.top
        && event.y <= chartArea.bottom

      if (!isInside) {
        hoveredDatasetIndex.value = null
        return
      }

      let minDistance = Number.POSITIVE_INFINITY
      let bestDatasetIndex: number | null = null
      const hoverThreshold = 25

      chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
        if (hiddenDatasets.value.includes(datasetIndex)) return
        const meta = chart.getDatasetMeta(datasetIndex)
        if (meta.hidden) return

        const points = meta.data
        if (!points || points.length === 0) return

        let datasetMinDist = Number.POSITIVE_INFINITY

        for (let i = 0; i < points.length - 1; i++) {
          const p1 = points[i]
          const p2 = points[i + 1]
          if (
            p1 && p2
            && typeof p1.x === 'number' && typeof p1.y === 'number'
            && typeof p2.x === 'number' && typeof p2.y === 'number'
          ) {
            const d = distToSegment(event.x, event.y, p1.x, p1.y, p2.x, p2.y)
            if (d < datasetMinDist) {
              datasetMinDist = d
            }
          }
        }

        if (points.length === 1) {
          const p0 = points[0]
          if (p0 && typeof p0.x === 'number' && typeof p0.y === 'number') {
            datasetMinDist = Math.hypot(event.x - p0.x, event.y - p0.y)
          }
        }

        if (datasetMinDist < minDistance) {
          minDistance = datasetMinDist
          bestDatasetIndex = datasetIndex
        }
      })

      if (bestDatasetIndex !== null && minDistance <= hoverThreshold) {
        hoveredDatasetIndex.value = bestDatasetIndex
      } else {
        hoveredDatasetIndex.value = null
      }
    },
    plugins: {
      ...baseConfig.plugins,
      tooltip: {
        ...(baseConfig.plugins as any)?.tooltip,
        intersect: true,
      },
      legend: {
        display: false,
      },
    },
  }
})

const distToSegment = (
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
) => {
  const dx = bx - ax
  const dy = by - ay
  if (dx === 0 && dy === 0) {
    return Math.hypot(px - ax, py - ay)
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)
  t = Math.max(0, Math.min(1, t))
  const projX = ax + t * dx
  const projY = ay + t * dy
  return Math.hypot(px - projX, py - projY)
}

const drawRoundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
}

const resolveYCollisions = (
  ideal: number[],
  minDist: number,
  chartArea: { top: number; bottom: number },
  tooltipHeight: number,
) => {
  const n = ideal.length
  if (n === 0) return []

  const pos = [...ideal]

  if (n === 1) {
    const minY = chartArea.top + 2
    const maxY = chartArea.bottom - 2 - tooltipHeight
    if (pos[0] < minY) pos[0] = minY
    if (pos[0] > maxY) pos[0] = maxY
    return pos
  }

  for (let iter = 0; iter < 30; iter++) {
    let changed = false
    for (let i = 0; i < n - 1; i++) {
      const dist = pos[i + 1] - pos[i]
      if (dist < minDist) {
        const overlap = minDist - dist
        pos[i] -= overlap / 2
        pos[i + 1] += overlap / 2
        changed = true
      }
    }
    if (!changed) break
  }

  const minY = chartArea.top + 2
  const maxY = chartArea.bottom - 2 - tooltipHeight
  const totalHeight = pos[n - 1] - pos[0]

  if (totalHeight > (maxY - minY)) {
    const step = (maxY - minY) / (n - 1 || 1)
    for (let i = 0; i < n; i++) {
      pos[i] = minY + i * step
    }
    return pos
  }

  if (pos[0] < minY) {
    const shift = minY - pos[0]
    for (let i = 0; i < n; i++) {
      pos[i] += shift
    }
  }

  if (pos[n - 1] > maxY) {
    const shift = pos[n - 1] - maxY
    for (let i = 0; i < n; i++) {
      pos[i] -= shift
    }
  }

  return pos
}

const drawTooltipWithLeader = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rectY: number,
  tooltipWidth: number,
  tooltipHeight: number,
  valueText: string,
  chartArea: { left: number; right: number; top: number; bottom: number },
  datasetColor: string,
) => {
  ctx.save()

  const fontName = 'Inter, sans-serif'
  let rectX = x - tooltipWidth / 2

  const minX = chartArea.left + 2
  const maxX = chartArea.right - 2
  if (rectX < minX) {
    rectX = minX
  }
  if (rectX + tooltipWidth > maxX) {
    rectX = maxX - tooltipWidth
  }

  const themeColors = vuetifyTheme.current.value.colors
  const isDark = vuetifyTheme.current.value.dark
  const tooltipBg = String(vuetifyTheme.current.value.variables['tooltip-background'] || themeColors.surface)
  const tooltipTextColor = isDark ? themeColors.background : themeColors['on-primary']

  ctx.fillStyle = datasetColor

  if (rectY + tooltipHeight <= y) {
    const gap = y - (rectY + tooltipHeight)
    if (gap < 12) {
      ctx.beginPath()
      ctx.moveTo(x, y - 3)
      ctx.lineTo(x - 4, rectY + tooltipHeight)
      ctx.lineTo(x + 4, rectY + tooltipHeight)
      ctx.closePath()
      ctx.fill()
    } else {
      ctx.beginPath()
      ctx.strokeStyle = datasetColor
      ctx.lineWidth = 1.5
      ctx.moveTo(x, rectY + tooltipHeight)
      ctx.lineTo(x, y - 3)
      ctx.stroke()
    }
  } else if (rectY >= y) {
    const gap = rectY - y
    if (gap < 12) {
      ctx.beginPath()
      ctx.moveTo(x, y + 3)
      ctx.lineTo(x - 4, rectY)
      ctx.lineTo(x + 4, rectY)
      ctx.closePath()
      ctx.fill()
    } else {
      ctx.beginPath()
      ctx.strokeStyle = datasetColor
      ctx.lineWidth = 1.5
      ctx.moveTo(x, rectY)
      ctx.lineTo(x, y + 3)
      ctx.stroke()
    }
  }

  ctx.fillStyle = tooltipBg
  ctx.strokeStyle = datasetColor
  ctx.lineWidth = 1.5
  ctx.beginPath()
  drawRoundRect(ctx, rectX, rectY, tooltipWidth, tooltipHeight, 4)
  ctx.fill()
  ctx.stroke()

  ctx.fillStyle = tooltipTextColor
  ctx.font = `bold 11px ${fontName}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(valueText, rectX + tooltipWidth / 2, rectY + tooltipHeight / 2)

  ctx.restore()
}

const allTooltipsPlugin: Plugin<any> = {
  id: 'allTooltips',
  afterDraw(chart) {
    if (!showValues.value) return

    const ctx = chart.ctx
    if (!ctx) return

    const chartArea = chart.chartArea
    if (!chartArea) return

    const groups: Record<number, {
      x: number
      y: number
      valueText: string
      tooltipWidth: number
      tooltipHeight: number
      idealY: number
      color: string
    }[]> = {}

    const fontName = 'Inter, sans-serif'
    ctx.save()
    ctx.font = `bold 11px ${fontName}`

    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
      if (hiddenDatasets.value.includes(datasetIndex)) return
      if (hoveredDatasetIndex.value !== null && hoveredDatasetIndex.value !== datasetIndex) return

      const meta = chart.getDatasetMeta(datasetIndex)
      if (meta.hidden) return

      const datasetColor = dataset.borderColor || dataset.backgroundColor || vuetifyTheme.current.value.colors.secondary

      meta.data.forEach((element: any, index: number) => {
        if (typeof element.tooltipPosition !== 'function') return

        const value = dataset.data[index]
        if (value === null || value === undefined || Number(value) === 0) return

        const { x, y } = element.tooltipPosition()
        const valueText = String(value)
        const textWidth = ctx.measureText(valueText).width
        const tooltipWidth = Math.max(22, textWidth + 10)
        const tooltipHeight = 19

        const idealY = y - 8 - tooltipHeight

        if (!groups[index]) {
          groups[index] = []
        }

        groups[index].push({
          x,
          y,
          valueText,
          tooltipWidth,
          tooltipHeight,
          idealY,
          color: datasetColor,
        })
      })
    })
    ctx.restore()

    Object.keys(groups).forEach(key => {
      const index = Number(key)
      const elements = groups[index]

      elements.sort((a, b) => a.idealY - b.idealY)

      const idealPositions = elements.map(el => el.idealY)
      const tHeight = elements[0]?.tooltipHeight || 19
      const minDist = tHeight + 4

      const resolvedPositions = resolveYCollisions(idealPositions, minDist, chartArea, tHeight)

      elements.forEach((el, i) => {
        const rectY = resolvedPositions[i]
        drawTooltipWithLeader(ctx, el.x, el.y, rectY, el.tooltipWidth, el.tooltipHeight, el.valueText, chartArea, el.color)
      })
    })
  },
}

const toggleDataset = (index: number) => {
  const hiddenIdx = hiddenDatasets.value.indexOf(index)
  if (hiddenIdx > -1) {
    hiddenDatasets.value.splice(hiddenIdx, 1)
  } else {
    hiddenDatasets.value.push(index)
    if (hoveredDatasetIndex.value === index) {
      hoveredDatasetIndex.value = null
    }
  }
}
</script>

<template>
  <VCard class="pa-6">
    <div class="d-flex align-center justify-space-between flex-wrap gap-2">
      <div class="text-h5 d-flex align-center gap-2">
        <div v-show="widgetData.show_icon && widgetData.icon" class="chart-icon-wrapper" :style="{ background: widgetData.icon_background }">
          <VIcon :icon="widgetData.icon" size="sm" />
        </div>
        {{ widgetData.name }}
      </div>
      <div class="d-flex align-center gap-4">
        <!-- Настройки графика -->
        <ChartSettingsMenu
          v-model:chart-type="chartType"
          v-model:show-values="showValues"
          v-model:selected-date-trunc="selectedDateTrunc"
          v-model:zoom-scale="zoomScale"
          :current-target="currentTarget"
        />

        <!-- Меню удаления виджета -->
        <VMenu
          v-if="isMonitoringRoute && !currentTarget"
          v-model="showMenu"
          :close-on-content-click="false"
        >
          <template #activator="{ props: menuProps }">
            <VIconBtn
              v-bind="menuProps"
              variant="plain"
              icon="ri-more-2-line"
            />
          </template>

          <VCard>
            <VList>
              <VListItem prepend-icon="ri-delete-bin-3-line" @click="deleteWidget(props.widgetData.id)">
                <VListItemTitle>Удалить</VListItemTitle>
              </VListItem>
            </VList>
          </VCard>
        </VMenu>
      </div>
    </div>
    <div class="text-body-1 mb-4">
      {{ widgetData.description }}
    </div>

    <!-- Кастомная интерактивная легенда -->
    <div class="custom-legend d-flex flex-wrap gap-x-6 justify-end mb-4 select-none">
      <div
        v-for="(dataset, index) in barData.datasets"
        :key="index"
        class="legend-item d-flex align-center cursor-pointer rounded gap-2"
        :class="{
          hovered: hoveredDatasetIndex === index,
          dimmed: hoveredDatasetIndex !== null && hoveredDatasetIndex !== index,
          disabled: hiddenDatasets.includes(index),
        }"
        @mouseenter="hoveredDatasetIndex = index"
        @mouseleave="hoveredDatasetIndex = null"
        @click="toggleDataset(index)"
      >
        <span
          class="legend-marker"
          :style="{ backgroundColor: originalColors[index] }"
        />
        <span class="legend-text font-weight-medium text-body-2 text-high-emphasis">{{ dataset.label }}</span>
      </div>
    </div>

    <div
      class="chart-zoom-wrapper"
      :style="{
        zoom: zoomScale !== 100 ? `${zoomScale}%` : undefined,
      }"
    >
      <div style="height: 400px;">
        <VSkeletonLoader
          v-if="loading"
          type="image"
          class="custom-chart-skeleton bg-transparent"
        />
        <template v-else>
          <BarChart
            v-if="chartType === 'bar'"
            :key="`${showValues ? 'bar-visible' : 'bar-hidden'}-${zoomScale}`"
            :height="400"
            :chart-data="barData"
            :chart-options="barChartOptions"
            :plugins="[allTooltipsPlugin as any]"
          />
          <LineChart
            v-else
            :key="`${showValues ? 'line-visible' : 'line-hidden'}-${zoomScale}`"
            :chart-options="lineChartConfig"
            :height="400"
            :chart-data="lineData"
            :plugins="[allTooltipsPlugin as any]"
          />
        </template>
      </div>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.custom-legend {
  .legend-item {
    padding: 4px 8px;
    transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: 0.85;

    &:hover, &.hovered {
      opacity: 1;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
      transform: translateY(-1px);

      .legend-text {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }

    &.dimmed {
      opacity: 0.3;
    }

    &.disabled {
      opacity: 0.25;

      .legend-text {
        text-decoration: line-through;
      }
      .legend-marker {
        background-color: #888 !important;
      }
    }
  }

  .legend-marker {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.15);
    transition: all 0.22s ease;
  }
}

.custom-chart-skeleton {
  height: 100%;
  :deep(.v-skeleton-loader__image) {
    height: 100% !important;
  }
}
</style>
